import React, { useMemo, useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Checkbox, CheckboxField } from '@/components/ui/catalyst/checkbox';
import { Field, Label } from '@/components/ui/catalyst/fieldset';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AssigneeRole,
  type CreateFirmRoleAssignmentSchema,
  type CreateFirmRoleAssignmentWithConfig,
  useCreateFirmRoleAssignmentMutation,
} from '@/services/api/firmsService';
import { useGetStaffUsersQuery } from '@/services/api/userService';
import { getUserDisplayName } from '@/utils/user';

type Props = {
  firmId: string;
  onCreated?: () => void;
  triggerLabel?: string;
  disabled?: boolean;
};

// All available role options
const ROLE_OPTIONS: { value: AssigneeRole; label: string }[] = [
  { value: 'managing_paralegal', label: 'Managing Paralegal' },
  { value: 'case_manager', label: 'Case Manager' },
  { value: 'police_report_manager', label: 'Police Report Manager' },
  { value: 'medical_records_manager', label: 'Medical Records Manager' },
  { value: 'demands_team', label: 'Demands Team' },
];

export const AddRoleAssignmentDialog: React.FC<Props> = ({
  firmId,
  onCreated,
  triggerLabel = 'Add Role Assignment',
  disabled,
}) => {
  const [open, setOpen] = useState(false);
  const [role, setRole] = useState<AssigneeRole | ''>('');
  const [assigneeId, setAssigneeId] = useState<string>('');
  const [caseAutoAssign, setCaseAutoAssign] = useState(false);

  const [createRoleAssignment, { isLoading }] =
    useCreateFirmRoleAssignmentMutation();

  const { data: users = [], isLoading: isLoadingUsers } =
    useGetStaffUsersQuery();

  const userOptions = useMemo(
    () =>
      users.map((user) => ({
        id: user.id,
        label: getUserDisplayName(user),
      })),
    [users],
  );

  const handleSave = async () => {
    if (!firmId) {
      toast.error('Please select a firm first');
      return;
    }
    if (!role || !assigneeId) {
      toast.error('Please select both role and assignee');
      return;
    }
    try {
      // Build the payload with config for case managers
      const payload: CreateFirmRoleAssignmentWithConfig = {
        firm_id: firmId,
        role: role as AssigneeRole,
        assignee_id: assigneeId,
      };

      // Add config for case manager role
      if (role === 'case_manager') {
        payload.config = {
          case_auto_assign: caseAutoAssign,
        };
      }

      await createRoleAssignment({
        firmPk: firmId,
        createFirmRoleAssignmentSchema:
          payload as unknown as CreateFirmRoleAssignmentSchema,
      }).unwrap();
      toast.success('Role assignment created');
      setOpen(false);
      setRole('');
      setAssigneeId('');
      setCaseAutoAssign(false);
      if (onCreated) onCreated();
    } catch (e: unknown) {
      const error = e as { data?: { detail?: string } };
      const msg = error?.data?.detail || 'Failed to create role assignment';
      toast.error(msg);
    }
  };

  const handleCancel = () => {
    setOpen(false);
    setRole('');
    setAssigneeId('');
    setCaseAutoAssign(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="secondary" disabled={disabled}>
          {triggerLabel}
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Add Role Assignment</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <Field>
            <Label htmlFor="role_select">Role</Label>
            <Select
              value={role}
              onValueChange={(val) => setRole(val as AssigneeRole)}
            >
              <SelectTrigger id="role_select">
                <SelectValue placeholder="Select a role" />
              </SelectTrigger>
              <SelectContent>
                {ROLE_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field>
            <Label htmlFor="assignee_select">Assignee</Label>
            <Select
              value={assigneeId}
              onValueChange={setAssigneeId}
              disabled={isLoadingUsers}
            >
              <SelectTrigger id="assignee_select">
                <SelectValue
                  placeholder={
                    isLoadingUsers ? 'Loading users...' : 'Select a user'
                  }
                />
              </SelectTrigger>
              <SelectContent>
                {userOptions.length === 0 && !isLoadingUsers && (
                  <SelectItem value="__empty__" disabled>
                    No users found
                  </SelectItem>
                )}
                {userOptions.map((opt) => (
                  <SelectItem key={opt.id} value={opt.id}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>

          {/* Per-role setting - show right after assignee when case manager */}
          {role === 'case_manager' && (
            <div>
              <CheckboxField>
                <Checkbox
                  checked={caseAutoAssign}
                  onChange={(checked) => setCaseAutoAssign(checked)}
                  color="indigo"
                />
                <Label>Auto-assign new matters to this person</Label>
              </CheckboxField>
              <p className="text-xs text-zinc-500 dark:text-zinc-400 ml-7 mt-1">
                When enabled, this case manager will automatically receive new
                matters via round-robin distribution.
              </p>
            </div>
          )}

        </div>
        <DialogFooter className="mt-4">
          <Button variant="ghost" onClick={handleCancel} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={isLoading || !role || !assigneeId}
          >
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
